import {atom, useAtomValue, useSetAtom} from 'jotai'
import {useEffect, useMemo, useRef} from 'react'
import useSWR, {type SWRResponse} from 'swr'

import type {Iri} from '../../@types/Api.ts'
import type {HydraCollection} from '../../@types/hydra/HydraCollection.ts'
import type {HydraCollectionMember} from '../../@types/hydra/HydraCollectionMember.ts'
import type {OptimisticError} from '../../@types/hydra/Optimistic.ts'
import {ApiError, isTransientError, NetworkError, TimeoutError, ViolationsError} from '../utils/ApiError.ts'
import {createCollectionIri} from '../utils/iri.ts'
import {nextOptimisticVersion} from '../utils/optimisticVersion.ts'

import {useGet} from './useApi.ts'
import {useAuthenticatedFetcher} from './useAuth.ts'
import {useErrorHandler} from './useErrorHandler'
import {useEvent} from './useEvent.ts'
import {useMutateResource, useMutateTags} from './useMutate.ts'
import {useOnlineStatus} from './useOnlineStatus'

const PENDING_IRI_PREFIX = '/__pending/'

type HydraCollectionMemberWithRequiredOptimistic = HydraCollectionMember & {
  readonly '@context'?: string
  readonly optimisticId: string
  readonly optimisticVersion: number
}
type HydraCollectionMemberWithOptimistic = HydraCollectionMember & {
  readonly optimisticId?: string
  readonly optimisticVersion?: number
}

type QueueItemType = 'insert' | 'update' | 'delete'

export type QueueFetcher = (url: string, init?: {method?: string, body?: string, headers?: Record<string, string>}) => Promise<any>

export interface QueueHookOptions {
  readonly fetcher?: QueueFetcher
}

export type QueueItem = {
  readonly type: QueueItemType
  readonly url: Iri
  readonly label: string
  readonly fetcher: QueueFetcher | undefined
  readonly group: string
  readonly optimisticId: string
  readonly payload: object & {
    readonly optimisticId: string
    readonly optimisticVersion: number
  }
  readonly processing: boolean
  readonly proceeded: boolean
  readonly attempted: boolean
  readonly failed: boolean
}

function createQueueItem(type: QueueItemType, url: Iri, label: string, optimisticId: string, optimisticVersion: number, payload: object, fetcher?: QueueFetcher, group?: string): QueueItem {
  return {
    type,
    url,
    label,
    fetcher,
    group: group ?? optimisticId,
    optimisticId,
    payload: {
      ...payload,
      optimisticId,
      optimisticVersion,
    },
    processing: false,
    proceeded: false,
    attempted: false,
    failed: false,
  }
}

function replaceItem(prev: QueueItem[], item: QueueItem, patch: Partial<QueueItem>): QueueItem[] {
  return prev.map((i) => i.optimisticId === item.optimisticId && i.payload.optimisticVersion === item.payload.optimisticVersion ? {...i, ...patch} : i)
}

function isRetryableError(error: unknown): boolean {
  return error instanceof TimeoutError
    || error instanceof NetworkError
    || (error instanceof ApiError && error.statusCode === 502)
}

const RETRY_DELAY = 30_000
const REMOVAL_DELAY = 5_000

function mergeQueue(prev: QueueItem[], newItem: QueueItem): QueueItem[] {
  const {optimisticId} = newItem
  const result: QueueItem[] = []
  let skipNewItem = false
  let enrichedNewItem = newItem

  for (const existing of prev) {
    // Keep items for other optimisticIds, or already processing/proceeded
    if (existing.optimisticId !== optimisticId || existing.processing || existing.proceeded) {
      result.push(existing)
      continue
    }

    if (!existing.attempted || existing.failed) {
      // Never attempted or failed (422) — safe to merge freely
      if (enrichedNewItem.type === 'update') {
        // post + update OR update + update → enrich first step, skip new
        result.push({
          ...existing,
          payload: {...existing.payload, ...enrichedNewItem.payload},
          attempted: false,
          failed: false,
        })
        skipNewItem = true
        continue
      }
      if (enrichedNewItem.type === 'delete') {
        if (existing.type === 'insert') {
          // post + delete → cancel both
          skipNewItem = true
          continue
        }
        // update + delete → remove update, keep delete
        continue
      }
    } else {
      // Attempted but not failed — be cautious (server might have processed it)
      if (enrichedNewItem.type === 'update') {
        if (existing.type === 'insert') {
          // post + update → DO NOTHING (post might have succeeded server-side)
          result.push(existing)
          continue
        }
        // update + update → remove first, enrich second
        enrichedNewItem = {
          ...enrichedNewItem,
          payload: {...existing.payload, ...enrichedNewItem.payload},
        }
        continue
      }
      if (enrichedNewItem.type === 'delete') {
        // post + delete OR update + delete → remove first, keep delete
        continue
      }
    }

    result.push(existing)
  }

  if (!skipNewItem) {
    result.push(enrichedNewItem)
  }

  return result
}

const queueAtom = atom<QueueItem[]>([])

// Latest optimistic per optimisticId (always the highest version)
const optimisticsAtom = atom<Map<string, HydraCollectionMemberWithRequiredOptimistic>>(new Map())

// IRI -> optimisticId mapping (both real IRIs and pending IRIs)
const iriToOptimisticIdAtom = atom<Map<Iri, string>>(new Map())

// Bumped when a new item is enqueued — used to wake up the processor without watching queue state
const enqueueSignalAtom = atom(0)

function useSetOptimisticError(): ((optimisticId: string, error: OptimisticError | undefined) => void) {
  const setOptimistics = useSetAtom(optimisticsAtom)

  return useEvent((optimisticId: string, error: OptimisticError | undefined) => {
    setOptimistics((prev) => {
      const existing = prev.get(optimisticId)
      if (!existing) {
        return prev
      }
      if (existing['@optimistic']?.error === error) {
        return prev
      }
      const next = new Map(prev)
      next.set(optimisticId, {
        ...existing,
        '@optimistic': {...existing['@optimistic']!, error},
      })
      return next
    })
  })
}

function useCleanupOptimistic(): ((queue: QueueItem[], optimisticId: string) => void) {
  const setOptimistics = useSetAtom(optimisticsAtom)
  const setIriMapping = useSetAtom(iriToOptimisticIdAtom)

  return useEvent((queue: QueueItem[], optimisticId: string) => {
    if (queue.some((item) => item.optimisticId === optimisticId)) {
      return
    }

    setOptimistics((prev) => {
      if (!prev.has(optimisticId)) {
        return prev
      }
      const next = new Map(prev)
      next.delete(optimisticId)
      return next
    })

    setIriMapping((prev) => {
      const next = new Map(prev)
      let changed = false
      for (const [iri, id] of prev) {
        if (id === optimisticId) {
          next.delete(iri)
          changed = true
        }
      }
      return changed ? next : prev
    })
  })
}

function useAsyncEnqueue(): ((queueItem: QueueItem, optimistic: HydraCollectionMemberWithRequiredOptimistic) => void) {
  const setQueue = useSetAtom(queueAtom)
  const setOptimistics = useSetAtom(optimisticsAtom)
  const setIriMapping = useSetAtom(iriToOptimisticIdAtom)
  const setEnqueueSignal = useSetAtom(enqueueSignalAtom)
  const cleanupOptimistic = useCleanupOptimistic()

  return useEvent((queueItem: QueueItem, optimistic: HydraCollectionMemberWithRequiredOptimistic) => {
    let mergedQueue: QueueItem[] = []
    setQueue((prev) => {
      mergedQueue = mergeQueue(prev, queueItem)
      return mergedQueue
    })

    const {optimisticId} = queueItem
    cleanupOptimistic(mergedQueue, optimisticId)

    if (!mergedQueue.some((item) => item.optimisticId === optimisticId)) {
      return
    }

    const {optimisticVersion} = optimistic

    setOptimistics((prev) => {
      const existing = prev.get(optimisticId)
      if (existing && existing.optimisticVersion >= optimisticVersion) {
        return prev
      }

      // If the new optimistic has a pending IRI but the existing one has a real IRI, keep the real IRI
      let iri = optimistic['@id']
      if (existing && iri.startsWith(PENDING_IRI_PREFIX) && !existing['@id'].startsWith(PENDING_IRI_PREFIX)) {
        iri = existing['@id']
      }

      const next = new Map(prev)
      next.set(optimisticId, {...optimistic, '@id': iri})
      return next
    })

    setIriMapping((prev) => {
      const iri = optimistic['@id']
      if (prev.get(iri) === optimisticId) {
        return prev
      }
      const next = new Map(prev)
      next.set(iri, optimisticId)
      return next
    })

    setEnqueueSignal((prev) => prev + 1)
  })
}

function useAsyncRemove(): ((queueItem: QueueItem) => void) {
  const setQueue = useSetAtom(queueAtom)
  const cleanupOptimistic = useCleanupOptimistic()

  return useEvent((queueItem: QueueItem) => {
    let remainingQueue: QueueItem[] = []
    setQueue((prev) => {
      remainingQueue = prev.filter((prevItem) =>
        prevItem.optimisticId !== queueItem.optimisticId
        || prevItem.payload.optimisticVersion !== queueItem.payload.optimisticVersion,
      )
      return remainingQueue
    })

    cleanupOptimistic(remainingQueue, queueItem.optimisticId)
  })
}

function useOptimistics(): Map<string, HydraCollectionMemberWithRequiredOptimistic> {
  return useAtomValue(optimisticsAtom)
}

function useIriToOptimisticId(): Map<Iri, string> {
  return useAtomValue(iriToOptimisticIdAtom)
}

export function useAsyncQueue(): QueueItem[] {
  return useAtomValue(queueAtom)
}

export function useAsyncOptimisticsByType<T extends HydraCollectionMember>(type: string): readonly T[] {
  const optimistics = useAtomValue(optimisticsAtom)
  return useMemo(() => {
    const items: T[] = []
    for (const item of optimistics.values()) {
      if (item['@type'] === type) {
        items.push(item as unknown as T)
      }
    }
    return items
  }, [optimistics, type])
}

function isOptimisticOutdated(
  serverVersion: number | undefined,
  optimistic: HydraCollectionMemberWithRequiredOptimistic,
  queue: QueueItem[],
): boolean {
  if (serverVersion === undefined) {
    return false
  }
  if (serverVersion === optimistic.optimisticVersion) {
    return true
  }
  if (serverVersion > optimistic.optimisticVersion) {
    return queue.every((item) => item.optimisticId !== optimistic.optimisticId || item.proceeded)
  }
  return false
}

const HTTP_METHODS: Record<QueueItemType, string> = {
  insert: 'POST',
  update: 'PATCH',
  delete: 'DELETE',
}

export function useAsyncQueueProcessor(): void {
  const queue = useAtomValue(queueAtom)
  const setQueue = useSetAtom(queueAtom)
  const enqueueSignal = useAtomValue(enqueueSignalAtom)
  const online = useOnlineStatus()
  const authenticatedFetcher = useAuthenticatedFetcher()
  const remove = useAsyncRemove()
  const reconcile = useReconcile()
  const setOptimisticError = useSetOptimisticError()
  const errorHandler = useErrorHandler()
  const mutateResource = useMutateResource()
  const mutateTags = useMutateTags()
  const optimistics = useOptimistics()
  const activeGroupsRef = useRef(new Set<string>())
  const queueRef = useRef(queue)
  const optimisticsRef = useRef(optimistics)
  // eslint-disable-next-line react-hooks/refs -- sync latest values for async processor
  optimisticsRef.current = optimistics
  const retryTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const retryResolversRef = useRef(new Map<string, () => void>())
  const removalTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  // eslint-disable-next-line react-hooks/refs -- sync latest values for async processor
  queueRef.current = queue

  const updateQueue = useEvent((updater: (prev: QueueItem[]) => QueueItem[]) => {
    setQueue((prev) => {
      const next = updater(prev)
      queueRef.current = next
      return next
    })
  })

  const cancelGroupRetry = useEvent((group: string) => {
    const timer = retryTimersRef.current.get(group)
    if (timer) {
      clearTimeout(timer)
      retryTimersRef.current.delete(group)
    }
  })

  const wakeUpGroup = useEvent((group: string) => {
    cancelGroupRetry(group)
    const resolver = retryResolversRef.current.get(group)
    if (resolver) {
      retryResolversRef.current.delete(group)
      resolver()
    }
  })

  const wakeUpAllGroups = useEvent(() => {
    for (const group of retryResolversRef.current.keys()) {
      wakeUpGroup(group)
    }
  })

  const processGroup = useEvent(async (group: string): Promise<void> => {
    let next: QueueItem | undefined
    while ((next = queueRef.current.find((item) => item.group === group && !item.proceeded && !item.failed))) {
      const current = next
      updateQueue((prev) => replaceItem(prev, current, {processing: true, attempted: true}))

      try {
        const method = HTTP_METHODS[current.type]
        const body = current.type !== 'delete' ? JSON.stringify(current.payload) : undefined
        const fetch = current.fetcher ?? authenticatedFetcher
        const response = await fetch(current.url, {method, body})

        // Reconcile: update pending IRIs on subsequent queue items and mark this one as proceeded
        if (response?.['@id']) {
          reconcile(current.optimisticId, response['@id'] as Iri, current.payload.optimisticVersion)
        }

        if (current.type === 'delete') {
          await mutateResource(current.url)
        } else if (response?.['@id']) {
          await mutateResource(response['@id'] as Iri, response)
        }
        if (response?.['@context']) {
          await mutateTags([response['@context'] as string])
        } else if (current.type === 'delete') {
          const optimistic = optimisticsRef.current.get(current.optimisticId)
          if (optimistic?.['@context']) {
            await mutateTags([optimistic['@context'] as string])
          }
        }

        updateQueue((prev) => replaceItem(prev, current, {processing: false, proceeded: true}))
      } catch (error) {
        if (isRetryableError(error)) {
          updateQueue((prev) => replaceItem(prev, current, {processing: false}))
          // Wait for: 30s timeout, network recovery, or new item queued
          await new Promise<void>((resolve) => {
            retryResolversRef.current.set(group, resolve)
            retryTimersRef.current.set(group, setTimeout(() => {
              retryTimersRef.current.delete(group)
              retryResolversRef.current.delete(group)
              resolve()
            }, RETRY_DELAY))
          })
          continue
        }
        if (error instanceof ViolationsError) {
          errorHandler(error)
          setOptimisticError(current.optimisticId, {message: error.message, violations: error.violations})
          updateQueue((prev) => replaceItem(prev, current, {processing: false, failed: true}))

          const failedTimer = setTimeout(() => {
            removalTimersRef.current.delete(failedTimer)
            remove(current)
          }, REMOVAL_DELAY)
          removalTimersRef.current.add(failedTimer)
          continue
        }
        // Non-retryable, non-validation error (e.g. 500, CORS, unexpected TypeError)
        if (error instanceof Error) errorHandler(error)
        setOptimisticError(current.optimisticId, {message: error instanceof Error ? error.message : String(error)})
        updateQueue((prev) => replaceItem(prev, current, {processing: false, proceeded: true}))
      }

      const timer = setTimeout(() => {
        removalTimersRef.current.delete(timer)
        remove(current)
      }, REMOVAL_DELAY)
      removalTimersRef.current.add(timer)
    }

    activeGroupsRef.current.delete(group)
  })

  const processQueue = useEvent(() => {
    const pending = queueRef.current.filter((item) => !item.proceeded && !item.failed && !item.processing)
    const newGroups = new Set<string>()
    for (const item of pending) {
      if (!activeGroupsRef.current.has(item.group)) {
        newGroups.add(item.group)
      }
    }

    for (const group of newGroups) {
      activeGroupsRef.current.add(group)
      void processGroup(group)
    }
  })

  useEffect(() => {
    if (!queue.some((item) => !item.proceeded && !item.failed)) {
      return
    }
    processQueue()
  }, [processQueue, queue])

  useEffect(() => {
    if (enqueueSignal > 0) {
      wakeUpAllGroups()
    }
  }, [enqueueSignal, wakeUpAllGroups])

  useEffect(() => {
    if (online) {
      wakeUpAllGroups()
    }
  }, [online, wakeUpAllGroups])

  useEffect(() => {
    const retryTimers = retryTimersRef.current
    const removalTimers = removalTimersRef.current
    return () => {
      for (const timer of retryTimers.values()) {
        clearTimeout(timer)
      }
      retryTimers.clear()
      for (const timer of removalTimers) {
        clearTimeout(timer)
      }
    }
  }, [])
}

const EMPTY_RECONCILIATIONS: ReconciliationEntry[] = []

const EMPTY_COLLECTION: HydraCollection<HydraCollectionMemberWithOptimistic> = {
  '@context': '',
  '@type': 'Collection',
  '@tags': [],
  totalItems: 0,
  member: [],
}

type ReconciliationEntry = {
  readonly optimisticId: string
  readonly realIri: Iri
  readonly serverVersion: number
}

function collectReconciliations(
  items: HydraCollectionMemberWithOptimistic[],
): ReconciliationEntry[] {
  const result: ReconciliationEntry[] = []
  for (const item of items) {
    if (!item.optimisticId || !item.optimisticVersion) {
      continue
    }
    if (item['@id'].startsWith(PENDING_IRI_PREFIX)) {
      continue
    }
    result.push({optimisticId: item.optimisticId, realIri: item['@id'], serverVersion: item.optimisticVersion})
  }
  return result.length > 0 ? result : EMPTY_RECONCILIATIONS
}

function useReconcile(): ((optimisticId: string, realIri: Iri, serverVersion: number) => void) {
  const setOptimistics = useSetAtom(optimisticsAtom)
  const setIriMapping = useSetAtom(iriToOptimisticIdAtom)
  const setQueue = useSetAtom(queueAtom)

  return useEvent((optimisticId: string, realIri: Iri, serverVersion: number) => {
    // Mark matching queue items as proceeded + update pending URLs to real IRI
    setQueue((prev) => {
      return prev.map((item) => {
        if (item.optimisticId !== optimisticId) {
          return item
        }
        const patch: {proceeded?: boolean, url?: Iri} = {}
        // use strict check on optimisticVersion, because the server might been updated by another user
        if (item.payload.optimisticVersion === serverVersion && !item.proceeded) {
          patch.proceeded = true
        }
        // Update URL on PATCH/DELETE items that still reference a pending IRI
        if (item.type !== 'insert' && item.url.startsWith(PENDING_IRI_PREFIX)) {
          patch.url = realIri
        }
        return Object.keys(patch).length > 0 ? {...item, ...patch} : item
      })
    })

    setOptimistics((prev) => {
      const existing = prev.get(optimisticId)
      if (!existing || !existing['@id'].startsWith(PENDING_IRI_PREFIX)) {
        return prev
      }
      const next = new Map(prev)
      next.set(optimisticId, {...existing, '@id': realIri})
      return next
    })

    setIriMapping((prev) => {
      if (prev.get(realIri) === optimisticId) {
        return prev
      }
      const next = new Map(prev)
      next.set(realIri, optimisticId)
      return next
    })
  })
}

function useApplyReconciliations(reconciliations: ReconciliationEntry[]): void {
  const reconcile = useReconcile()

  useEffect(() => {
    for (const entry of reconciliations) {
      reconcile(entry.optimisticId, entry.realIri, entry.serverVersion)
    }
  }, [reconcile, reconciliations])
}

export function useAsyncCollection<Data = any, Error = any>(key: string | undefined | null, match: (item: HydraCollectionMember) => boolean): SWRResponse<Data, Error | ApiError> {
  const swrResponse = useSWR(key)
  const optimisticsById = useOptimistics()
  const iriMapping = useIriToOptimisticId()
  const queue = useAtomValue(queueAtom)

  const matched = useMemo((): Map<string, HydraCollectionMemberWithRequiredOptimistic> => {
    return new Map(
      [...optimisticsById].filter(([, optimistic]) => match(optimistic)),
    )
    // `match` is a stable `useEvent` identity and cannot signal a filter change; `key` encodes the same filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match, optimisticsById, key])

  const data = (swrResponse.data as HydraCollection<HydraCollectionMemberWithOptimistic> | undefined)
    ?? (swrResponse.error && isTransientError(swrResponse.error) && matched.size > 0 ? EMPTY_COLLECTION : undefined)

  const reconciliations = useMemo(
    () => data ? collectReconciliations(data.member) : EMPTY_RECONCILIATIONS,
    [data],
  )
  useApplyReconciliations(reconciliations)

  const merge = useMemo(() => {
    if (!data) {
      return data
    }
    const available = new Map(matched)

    const member = data.member.reduce<HydraCollectionMemberWithOptimistic[]>((acc, item) => {
      // look up optimistic: first by IRI, then by optimisticId
      const optimisticId = item.optimisticId ?? iriMapping.get(item['@id'])
      const optimistic = optimisticId ? matched.get(optimisticId) : undefined

      if (!optimistic) {
        acc.push(item)
        return acc
      }

      available.delete(optimistic.optimisticId)
      if (optimistic['@optimistic']?.type === 'delete') {
        return acc
      }
      if (isOptimisticOutdated(item.optimisticVersion, optimistic, queue)) {
        acc.push(item)
        return acc
      }

      acc.push({...item, ...optimistic})
      return acc
    }, [])

    for (const optimistic of available.values()) {
      if (optimistic['@optimistic']?.type === 'delete') {
        continue
      }
      member.push(optimistic)
    }

    return {
      ...data,
      member,
      totalItems: member.length,
    }
  }, [data, iriMapping, matched, queue])

  if (!data) {
    return swrResponse
  }

  return {
    ...swrResponse,
    data: merge as Data,
  }
}

export function useAsyncGet<Data = any, Error = any>(key: string | undefined | null): SWRResponse<Data, Error | ApiError> {
  const isPending = (key ?? '').startsWith(PENDING_IRI_PREFIX)
  const swrResponse = useGet(isPending ? undefined : key)
  const data = swrResponse.data as HydraCollectionMemberWithOptimistic
  const optimisticsById = useOptimistics()
  const iriMapping = useIriToOptimisticId()
  const queue = useAtomValue(queueAtom)

  const reconciliations = useMemo(
    () => data ? collectReconciliations([data]) : EMPTY_RECONCILIATIONS,
    [data],
  )
  useApplyReconciliations(reconciliations)

  const optimistic = useMemo((): HydraCollectionMemberWithRequiredOptimistic | undefined => {
    // look up by IRI (works for both pending and real IRIs)
    const optimisticId = (key ? iriMapping.get(key as Iri) : undefined) ?? data?.optimisticId
    if (!optimisticId) {
      return undefined
    }

    return optimisticsById.get(optimisticId)
  }, [data, iriMapping, key, optimisticsById])

  if (!optimistic) {
    return swrResponse
  }

  if (optimistic['@optimistic']?.type === 'delete') {
    return {
      ...swrResponse,
      data: undefined,
    }
  }
  if (isOptimisticOutdated(data?.optimisticVersion, optimistic, queue)) {
    return swrResponse
  }

  return {
    ...swrResponse,
    data: {
      ...data,
      ...optimistic,
    } as Data,
  }
}

export function useAsyncPost<T extends HydraCollectionMemberWithOptimistic>(resource: string, hookOptions?: QueueHookOptions): ((payload: object, optimistic: T, label: string, group?: string) => T) {
  const enqueue = useAsyncEnqueue()
  const url = createCollectionIri(resource)

  return useEvent((payload: object, optimistic: T, label: string, group?: string): T => {
    const optimisticId = crypto.randomUUID()
    const optimisticVersion = nextOptimisticVersion()

    const enrichedOptimistic = {
      ...optimistic,
      '@id': `${PENDING_IRI_PREFIX}${optimisticId}` as Iri,
      optimisticId,
      optimisticVersion,
      '@optimistic': {
        type: 'insert' as const,
      },
    }

    enqueue(createQueueItem('insert', url, label, optimisticId, optimisticVersion, payload, hookOptions?.fetcher, group), enrichedOptimistic)

    return enrichedOptimistic
  })
}

export function useAsyncPatch<T extends HydraCollectionMemberWithOptimistic>(hookOptions?: QueueHookOptions): ((existing: HydraCollectionMemberWithOptimistic, payload: object, optimistic: T, label: string, group?: string) => T) {
  const enqueue = useAsyncEnqueue()

  return useEvent((existing: HydraCollectionMemberWithOptimistic, payload: object, optimistic: T, label: string, group?: string): T => {
    const optimisticId = existing.optimisticId ?? crypto.randomUUID()
    const optimisticVersion = nextOptimisticVersion()

    const enrichedOptimistic = {
      ...existing,
      ...optimistic,
      '@id': existing['@id'],
      optimisticId,
      optimisticVersion,
      '@optimistic': {
        type: 'update' as const,
      },
    }

    enqueue(createQueueItem('update', existing['@id'], label, optimisticId, optimisticVersion, payload, hookOptions?.fetcher, group), enrichedOptimistic)

    return enrichedOptimistic
  })
}

export function useAsyncDelete(hookOptions?: QueueHookOptions): ((existing: HydraCollectionMemberWithOptimistic, label: string, group?: string) => void) {
  const enqueue = useAsyncEnqueue()

  return useEvent((existing: HydraCollectionMemberWithOptimistic, label: string, group?: string): void => {
    const optimisticId = existing.optimisticId ?? crypto.randomUUID()
    const optimisticVersion = nextOptimisticVersion()

    const enrichedOptimistic = {
      ...existing,
      optimisticId,
      optimisticVersion,
      '@optimistic': {
        type: 'delete' as const,
      },
    }

    enqueue(createQueueItem('delete', existing['@id'], label, optimisticId, optimisticVersion, {}, hookOptions?.fetcher, group), enrichedOptimistic)
  })
}
